import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import {
  FEEDBACK_SOURCE_LABEL,
  createFeedbackSubmissionId,
  getFeedbackLocationContext,
  submitFeedback,
  toFeedbackMediaPayload,
} from '../../api/feedback';
import {
  FEEDBACK_MEDIA_POLICIES,
  FEEDBACK_TYPE_OPTIONS,
  type FeedbackMediaSlot,
  type FeedbackType,
  describeAcceptedTypes,
  formatBytes,
  slotLabel,
  validateMediaFile,
  validateRequiredFields,
} from './feedbackForm';

interface MediaState {
  file?: File;
  error?: string;
  pending?: boolean;
  durationSeconds?: number;
}

interface FieldErrors {
  feedbackType?: string;
  writtenFeedback?: string;
}

type SubmissionState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'succeeded'; journeyItemId: string }
  | { status: 'failed'; message: string; retryable: boolean };

const initialMediaState: Record<FeedbackMediaSlot, MediaState> = {
  screenshot: {},
  screen_recording: {},
};

export function FeedbackPage() {
  const [feedbackType, setFeedbackType] = useState<FeedbackType | ''>('');
  const [writtenFeedback, setWrittenFeedback] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [media, setMedia] = useState<Record<FeedbackMediaSlot, MediaState>>(initialMediaState);
  const [submissionState, setSubmissionState] = useState<SubmissionState>({ status: 'idle' });
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const resetSubmissionBoundary = () => {
    setSubmissionState({ status: 'idle' });
    setSubmissionId(null);
  };
  const controlsDisabled =
    submissionState.status === 'submitting' || submissionState.status === 'succeeded';

  const handleMediaChange =
    (slot: FeedbackMediaSlot) => async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      resetSubmissionBoundary();

      if (!file) {
        setMedia((current) => ({ ...current, [slot]: {} }));
        return;
      }

      const validation = validateMediaFile(slot, file);
      if (!validation.valid) {
        setMedia((current) => ({
          ...current,
          [slot]: { file, error: validation.message },
        }));
        return;
      }

      if (slot === 'screen_recording') {
        setMedia((current) => ({ ...current, [slot]: { file, pending: true } }));
        const durationSeconds = await readVideoDuration(file);
        const durationValidation = validateMediaFile(slot, file, durationSeconds ?? undefined);

        setMedia((current) => ({
          ...current,
          [slot]: durationValidation.valid
            ? { file, durationSeconds: durationSeconds ?? undefined }
            : { file, error: durationValidation.message },
        }));
        return;
      }

      setMedia((current) => ({ ...current, [slot]: { file } }));
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (submittingRef.current || submissionState.status === 'succeeded') {
      return;
    }

    const nextFieldErrors = validateRequiredFields({ feedbackType, writtenFeedback });
    setFieldErrors(nextFieldErrors);

    const hasFieldErrors = Object.values(nextFieldErrors).some(Boolean);
    const hasMediaErrors = Object.values(media).some(({ error, pending }) => error || pending);

    if (hasFieldErrors || hasMediaErrors) {
      setSubmissionState({ status: 'idle' });
      return;
    }

    if (!feedbackType) {
      return;
    }

    const currentSubmissionId = submissionId ?? createFeedbackSubmissionId();
    setSubmissionId(currentSubmissionId);
    setSubmissionState({ status: 'submitting' });
    submittingRef.current = true;

    try {
      const mediaPayload = await Promise.all(
        Object.entries(media)
          .filter((entry): entry is [FeedbackMediaSlot, MediaState & { file: File }] =>
            Boolean(entry[1].file)
          )
          .map(([slot, state]) =>
            toFeedbackMediaPayload({
              slot,
              file: state.file,
              durationSeconds: state.durationSeconds,
            })
          )
      );

      const response = await submitFeedback({
        submission_id: currentSubmissionId,
        feedback_type: feedbackType,
        written_feedback: writtenFeedback,
        source_label: FEEDBACK_SOURCE_LABEL,
        location_context: getFeedbackLocationContext(),
        media: mediaPayload.length > 0 ? mediaPayload : undefined,
      });

      setSubmissionState({ status: 'succeeded', journeyItemId: response.journey_item_id });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Feedback submission failed. Please review and try again.';
      const retryable =
        typeof error === 'object' &&
        error !== null &&
        'retryable' in error &&
        typeof error.retryable === 'boolean'
          ? error.retryable
          : true;

      setSubmissionState({ status: 'failed', message, retryable });
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <form
          className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-8"
          onSubmit={handleSubmit}
          noValidate
        >
          <p className="text-sm font-medium text-blue-700">ABPL UPC Management</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">ABPL UPC Feedback</h1>

          <fieldset className="mt-8">
            <legend className="text-base font-semibold text-gray-900">
              Feedback type <span className="text-red-600">*</span>
            </legend>
            {fieldErrors.feedbackType ? (
              <p className="mt-2 text-sm text-red-700" role="alert">
                {fieldErrors.feedbackType}
              </p>
            ) : null}
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {FEEDBACK_TYPE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer flex-col rounded-md border p-4 transition-colors ${
                    feedbackType === option.value
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <input
                      type="radio"
                      name="feedback_type"
                      value={option.value}
                      checked={feedbackType === option.value}
                      disabled={controlsDisabled}
                      onChange={() => {
                        setFeedbackType(option.value);
                        setFieldErrors((current) => ({ ...current, feedbackType: undefined }));
                        resetSubmissionBoundary();
                      }}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600"
                    />
                    {option.label}
                  </span>
                  <span className="mt-2 text-sm text-gray-600">{option.description}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="mt-8">
            <label htmlFor="written-feedback" className="text-base font-semibold text-gray-900">
              Written feedback <span className="text-red-600">*</span>
            </label>
            <textarea
              id="written-feedback"
              name="written_feedback"
              value={writtenFeedback}
              disabled={controlsDisabled}
              onChange={(event) => {
                setWrittenFeedback(event.target.value);
                setFieldErrors((current) => ({ ...current, writtenFeedback: undefined }));
                resetSubmissionBoundary();
              }}
              rows={6}
              aria-invalid={Boolean(fieldErrors.writtenFeedback)}
              aria-describedby={fieldErrors.writtenFeedback ? 'written-feedback-error' : undefined}
              className={`mt-3 w-full rounded-md border px-3 py-2 text-base text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                fieldErrors.writtenFeedback
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
            {fieldErrors.writtenFeedback ? (
              <p id="written-feedback-error" className="mt-2 text-sm text-red-700" role="alert">
                {fieldErrors.writtenFeedback}
              </p>
            ) : null}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <MediaPicker
              slot="screenshot"
              state={media.screenshot}
              onChange={handleMediaChange('screenshot')}
              disabled={controlsDisabled}
            />
            <MediaPicker
              slot="screen_recording"
              state={media.screen_recording}
              onChange={handleMediaChange('screen_recording')}
              disabled={controlsDisabled}
            />
          </div>

          {submissionState.status === 'succeeded' ? (
            <p
              className="mt-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800"
              role="status"
            >
              Feedback submitted to Journey.
            </p>
          ) : null}

          {submissionState.status === 'failed' ? (
            <p
              className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800"
              role="alert"
            >
              {submissionState.message}
              {submissionState.retryable
                ? ' Your written feedback is still available to retry.'
                : ''}
            </p>
          ) : null}

          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={controlsDisabled}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submissionState.status === 'submitting' ? 'Submitting' : 'Submit feedback'}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

function MediaPicker({
  slot,
  state,
  onChange,
  disabled,
}: {
  slot: FeedbackMediaSlot;
  state: MediaState;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
}) {
  const policy = FEEDBACK_MEDIA_POLICIES[slot];
  const inputId = `${slot}-input`;
  const errorId = `${slot}-error`;
  const helpId = `${slot}-help`;
  const label = slotLabel(slot);

  return (
    <div className="rounded-md border border-gray-200 p-4">
      <label htmlFor={inputId} className="text-sm font-semibold text-gray-900">
        {label}
      </label>
      <p id={helpId} className="mt-1 text-sm text-gray-600">
        Optional. {describeAcceptedTypes(policy)} up to {formatBytes(policy.maxFileSizeBytes)}
        {policy.maxDurationSeconds ? ` and ${policy.maxDurationSeconds} seconds` : ''}.
      </p>
      <input
        id={inputId}
        type="file"
        accept={policy.acceptedMimeTypes.join(',')}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={`${helpId}${state.error ? ` ${errorId}` : ''}`}
        aria-invalid={Boolean(state.error)}
        className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-800 hover:file:bg-gray-200"
      />
      {state.pending ? (
        <p className="mt-2 text-sm text-gray-600">Checking recording length.</p>
      ) : null}
      {state.file && !state.error && !state.pending ? (
        <p className="mt-2 text-sm font-medium text-green-700">{state.file.name} is attached.</p>
      ) : null}
      {state.error ? (
        <p id={errorId} className="mt-2 text-sm font-medium text-red-700" role="alert">
          {state.error}
        </p>
      ) : null}
    </div>
  );
}

async function readVideoDuration(file: File): Promise<number | null> {
  if (typeof URL.createObjectURL !== 'function') {
    return null;
  }

  return await new Promise((resolve) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const durationSeconds = Number.isFinite(video.duration) ? video.duration : null;
      cleanup();
      resolve(durationSeconds);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = objectUrl;
  });
}
